import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Save, RotateCcw, X, Trash2, Eye, ArrowLeft, MapPin } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import BeachMap from "@/components/BeachMap";
import { InlineLoader } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SIZE_DIM = { small: 74, medium: 92, large: 120 };
const DEFAULT_BALDACHIN_DIM = 92;
const NEW_BALDACHIN_SPAWN = { x: 40, y: 40 };
const RESET_GRID = { perRow: 15, xStart: 60, xStep: 104, yStart: 70, yStep: 124 };
const EDITOR_MAP_MIN_HEIGHT = 600;
const PREVIEW_MAP_MIN_HEIGHT = 560;
const MAP_ZOOM = 0.8;

export default function MapEditor() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [preview, setPreview] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/baldachins").then((r) => setItems(r.data.map((b) => ({ ...b })))).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => items?.find((i) => i.id === selectedId), [items, selectedId]);

  const updateSelected = (patch) => {
    setItems((prev) => prev.map((i) => (i.id === selectedId ? { ...i, ...patch } : i)));
  };

  const onMove = (id, x, y) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, x, y } : i)));
  };

  const addBaldachin = () => {
    const maxNum = items.reduce((m, i) => Math.max(m, parseInt(i.number, 10) || 0), 0);
    const newItem = {
      id: `new-${Date.now()}`,
      number: String(maxNum + 1),
      x: NEW_BALDACHIN_SPAWN.x, y: NEW_BALDACHIN_SPAWN.y, w: DEFAULT_BALDACHIN_DIM, h: DEFAULT_BALDACHIN_DIM, size: "medium", zone: "New",
    };
    setItems((prev) => [...prev, newItem]);
    setSelectedId(newItem.id);
    toast.success(`Baldachin ${newItem.number} added — drag it into place`);
  };

  const removeSelected = () => {
    if (!selected) return;
    setItems((prev) => prev.filter((i) => i.id !== selectedId));
    setSelectedId(null);
  };

  const resetLayout = () => {
    setItems((prev) => {
      const { perRow, xStart, xStep, yStart, yStep } = RESET_GRID;
      return prev.map((i, idx) => ({
        ...i,
        x: xStart + (idx % perRow) * xStep,
        y: yStart + Math.floor(idx / perRow) * yStep,
      }));
    });
    toast.message("Layout auto-arranged into a grid. Save to keep it.");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        baldachins: items.map((i) => ({
          id: typeof i.id === "string" && i.id.startsWith("new-") ? null : i.id,
          number: String(i.number),
          x: i.x, y: i.y, w: i.w, h: i.h, size: i.size, zone: i.zone || null,
        })),
      };
      const r = await api.put("/layout", payload);
      setItems(r.data.map((b) => ({ ...b })));
      setSelectedId(null);
      toast.success("Layout saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !items) return <InlineLoader label="Loading layout…" />;

  return (
    <div className="space-y-4 bb-fade-up">
      <button onClick={() => navigate("/admin/map")} className="flex items-center gap-1.5 text-sm font-semibold text-stone-500 hover:text-stone-800" data-testid="editor-back">
        <ArrowLeft className="h-4 w-4" /> Back to Live Map
      </button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Map Editor</h1>
          <p className="text-sm text-stone-500">{items.length} baldachins · drag cards to match the real beach</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setPreview((p) => !p)} data-testid="editor-preview" className="gap-2 rounded-xl font-semibold">
            <Eye className="h-4 w-4" /> {preview ? "Exit Preview" : "Preview Worker Map"}
          </Button>
          {!preview && (
            <>
              <Button variant="outline" onClick={addBaldachin} data-testid="editor-add" className="gap-2 rounded-xl font-semibold">
                <Plus className="h-4 w-4" /> Add
              </Button>
              <Button variant="outline" onClick={resetLayout} data-testid="editor-reset" className="gap-2 rounded-xl font-semibold">
                <RotateCcw className="h-4 w-4" /> Reset
              </Button>
              <Button variant="outline" onClick={load} data-testid="editor-cancel" className="gap-2 rounded-xl font-semibold">
                <X className="h-4 w-4" /> Cancel
              </Button>
              <Button onClick={save} disabled={saving} data-testid="editor-save" className="gap-2 rounded-xl font-semibold">
                <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Layout"}
              </Button>
            </>
          )}
        </div>
      </div>

      {preview ? (
        <div>
          <div className="mb-2 rounded-xl bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700">Preview — this is how workers see the map (not editable).</div>
          <BeachMap baldachins={items.map((i) => ({ ...i, status: "free", count: 0 }))} mode="worker" minHeight={PREVIEW_MAP_MIN_HEIGHT} initialZoom={MAP_ZOOM} />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_300px]">
          <BeachMap
            baldachins={items.map((i) => ({ ...i, status: "free", count: 0 }))}
            mode="editor"
            editable
            onMove={onMove}
            selectedId={selectedId}
            onCardClick={(b) => setSelectedId(b.id)}
            minHeight={EDITOR_MAP_MIN_HEIGHT}
            initialZoom={MAP_ZOOM}
          />

          <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="editor-selected-panel">
            {!selected ? (
              <div className="flex flex-col items-center gap-2 py-10 text-center text-stone-400">
                <MapPin className="h-8 w-8" strokeWidth={1.8} />
                <p className="text-sm font-medium">Tap a baldachin to edit it, or drag to reposition.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-lg font-bold">Baldachin {selected.number}</h2>
                  <button onClick={removeSelected} data-testid="editor-remove" className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold text-red-600 hover:bg-red-50">
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold">Number / Name</Label>
                  <Input value={selected.number} onChange={(e) => updateSelected({ number: e.target.value })} data-testid="editor-number-input" />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold">Size</Label>
                  <Select value={selected.size} onValueChange={(v) => updateSelected({ size: v, w: SIZE_DIM[v], h: SIZE_DIM[v] })}>
                    <SelectTrigger data-testid="editor-size-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Small</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="large">Large</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold">Zone / Area label</Label>
                  <Input value={selected.zone || ""} onChange={(e) => updateSelected({ zone: e.target.value })} data-testid="editor-zone-input" placeholder="e.g. Beachfront" />
                </div>

                <div className="rounded-xl bg-stone-50 p-3 text-xs text-stone-500">
                  <p>Position: X {Math.round(selected.x)} · Y {Math.round(selected.y)}</p>
                  <p>Size: {selected.w}×{selected.h}px</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
